/**
 * PlanPill — small visible plan indicator (Free / Premium) shown near the avatar.
 * Always-visible plan status. Clicking navigates to /profile?tab=subscription.
 */
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIsPremium } from '@/hooks/useIsPremium';
import { cn } from '@/lib/utils';

interface PlanPillProps {
  className?: string;
  compact?: boolean;
}

export function PlanPill({ className, compact = false }: PlanPillProps) {
  const navigate = useNavigate();
  const { isPremium, loading } = useIsPremium();

  if (loading) return null;

  const handleClick = () => navigate('/profile?tab=subscription');

  if (isPremium) {
    return (
      <button
        onClick={handleClick}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 font-semibold text-primary transition-colors hover:bg-primary/20',
          compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs',
          className,
        )}
        aria-label="Premium plan — manage subscription"
      >
        <Sparkles className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        Premium
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'inline-flex items-center rounded-full border border-border bg-muted font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground',
        compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs',
        className,
      )}
      aria-label="Free plan — upgrade"
    >
      Free
    </button>
  );
}
